import {
  AnchorButtonProps,
  Callout,
  Intent,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';

import { useMessageLineInfo } from 'src/containers/Voicebox/Message/hooks/useMessageLineInfo';
import { MessageButton } from 'src/containers/Voicebox/Message/MessageButton';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { addVoiceboxModel } from 'src/utils/voicebox/addVoiceboxModel';
import { VoiceboxDescribeResponseMessage } from 'src/utils/voicebox/types';

export const AssistantMessage: React.VFC<{
  after?: string;
  before?: string;
  buttonProps?: AnchorButtonProps;
  buttonTooltip?: string;
  codeElement?: React.ReactNode;
  disabled?: boolean;
  errored?: boolean;
  pending?: boolean;
}> = ({
  after,
  before,
  buttonProps,
  buttonTooltip,
  codeElement,
  disabled,
  errored,
  pending,
}) => {
  const button = (
    <MessageButton
      buttonProps={buttonProps}
      buttonTooltip={buttonTooltip}
      disabled={disabled}
      pending={pending}
      tooltipPosition={Position.RIGHT}
    />
  );

  if (!after && !before && !codeElement) {
    return button;
  }

  return (
    <div className="sd-voicebox__message__assistant">
      <Callout
        className="sd-voicebox__message__assistant__callout"
        icon={null}
        intent={errored ? Intent.DANGER : Intent.NONE}
      >
        <div>{before}</div>
        <div>{codeElement}</div>
        <div>{after}</div>
      </Callout>
      <div className="sd-voicebox__message__assistant__right-elements">
        {button}
      </div>
    </div>
  );
};

export const AssistantMessageWithError: React.VFC<{
  before: string;
  pending: boolean;
}> = ({ before, pending }) => {
  return <AssistantMessage before={before} errored pending={pending} />;
};

export const AssistantMessageWithCode: React.VFC<{
  after: string;
  before: string;
  code: string;
  describeResponseMessage?: VoiceboxDescribeResponseMessage;
  pending: boolean;
}> = ({ after, before, code, describeResponseMessage, pending }) => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const { disabled, disabledReason } = useMessageLineInfo();

  let codeElement;
  if (describeResponseMessage) {
    if (describeResponseMessage.errored) {
      codeElement = (
        <Callout icon={null} intent={Intent.DANGER}>
          {describeResponseMessage.content}
        </Callout>
      );
    } else {
      codeElement = (
        <Callout icon={null} intent={Intent.PRIMARY}>
          {describeResponseMessage.content}
        </Callout>
      );
    }
  }

  const handleButtonClick = React.useCallback(async () => {
    if (!code) {
      return;
    }

    const success = dispatch(addVoiceboxModel({ code, projectId }));
    if (success) {
      dispatch(uiSlice.actions.toggledVoiceboxDialog({ isOpen: false }));
    }
  }, [code, dispatch, projectId]);

  const buttonProps = code
    ? { icon: IconNames.ADD, onClick: handleButtonClick }
    : undefined;

  return (
    <AssistantMessage
      after={after}
      before={before}
      buttonProps={buttonProps}
      buttonTooltip={disabledReason}
      codeElement={codeElement}
      disabled={disabled}
      pending={pending}
    />
  );
};
